import { derivePhones } from '@/utils/derivePhones';
import { summarizeOutboundActions, getRecipientBusiness } from '@/utils/outboundText';

interface CallAction {
  id: string;
  call_id: string;
  campaign_id: string;
  prospect_id: string;
  type: string;
  confidence: number;
  phrase: string | null;
  action_data: any;
  processed: boolean;
  created_at: string;
  resolved_intelligence?: any;
  campaign?: {
    name: string;
    type?: string;
    businessContext?: {
      company: string;
      industry: string;
      context: string;
    };
  };
  mode?: string;
  campaigns?: {
    campaign_type: string;
  };
}

interface ContactInfo {
  name?: string;
  phone?: string;
  email?: string;
  company?: string;
}

interface BusinessContext {
  name: string;
  businessHeader: string;
  phone?: string;
  email?: string;
  company?: string;
}

export function extractContactIntelligence(actions: CallAction[], vapiTranscript?: string): BusinessContext {
  const latestAction = actions.sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )[0];
  
  const isInbound = actions.some(a => a.mode === 'inbound');
  
  // Extract basic contact info
  const contactInfo = extractContactInfo(actions);
  
  if (isInbound && vapiTranscript) {
    // For inbound calls, extract intelligence from transcript
    return extractInboundIntelligence(contactInfo, vapiTranscript, latestAction);
  } else {
    // For outbound calls, also parse transcript if available
    return extractOutboundIntelligence(contactInfo, latestAction, vapiTranscript, actions);
  }
}

function extractInboundIntelligence(contactInfo: ContactInfo, transcript: string, action: CallAction): BusinessContext {
  // Extract caller name from transcript or resolved intelligence
  const callerName = extractCallerName(transcript) || 
    action.resolved_intelligence?.caller_name || 
    contactInfo.name || 'Unknown Contact';
  
  // Extract phone number from transcript
  const phoneNumber = extractPhoneFromTranscript(transcript) || contactInfo.phone;
  
  // Extract business context from transcript
  const businessContext = extractBusinessIntent(transcript);
  
  // Create business header: "Doug - Proposal Follow-up" or "Sarah - Marketing Campaign Setup"
  let businessHeader = callerName;
  if (businessContext) {
    businessHeader = `${callerName} - ${businessContext}`;
  }
  
  return {
    name: callerName,
    businessHeader,
    phone: phoneNumber,
    email: contactInfo.email,
    company: contactInfo.company
  };
}

function extractOutboundIntelligence(contactInfo: ContactInfo, action: CallAction, vapiTranscript?: string, allActions?: CallAction[]): BusinessContext {
  const campaign = action.campaign;
  
  // First, try to extract from transcript if available (same as inbound)
  let transcriptName: string | null = null;
  let transcriptPhone: string | null = null;
  let transcriptEmail: string | null = null;
  let businessContext: string | null = null;
  
  if (vapiTranscript) {
    transcriptName = extractCallerName(vapiTranscript);
    transcriptPhone = extractPhoneFromTranscript(vapiTranscript);
    transcriptEmail = extractEmailFromTranscript(vapiTranscript);
    businessContext = extractBusinessIntent(vapiTranscript);

    if (process.env.NODE_ENV === 'development') {
      console.debug('Outbound transcript extraction:', {
        transcriptName,
        transcriptPhone,
        transcriptEmail,
        businessContext
      });
    }
  }
  
  // Prioritize transcript data over contact info, then fallback to prospect record or Vapi data
  const name = transcriptName || contactInfo.name || 'Unknown Contact';
  const phone = transcriptPhone || contactInfo.phone || action.action_data?.phone_number;
  const email = transcriptEmail || contactInfo.email;
  
  // Build business header using outbound text utilities
  const actions = allActions || [action];
  const { summary } = summarizeOutboundActions(actions);
  
  // Build recipient business context - show business that RECEIVED the call
  let recipientBusiness = getRecipientBusiness(actions);
  
  // If no recipient business found, fallback to campaign business context for industry
  if (!recipientBusiness && campaign?.businessContext?.industry) {
    recipientBusiness = campaign.businessContext.industry;
  }
  
  // If still no business context, extract from campaign name
  if (!recipientBusiness && campaign?.name) {
    const campaignMatch = campaign.name.match(/\(([^)]+)\)/);
    if (campaignMatch) {
      recipientBusiness = campaignMatch[1];
    }
  }
  
  // Build business header with recipient business context
  let businessHeader = name;
  if (summary !== 'Contact made') {
    businessHeader = `${name} - ${summary}`;
  } else if (recipientBusiness) {
    businessHeader = `${name} - ${recipientBusiness}`;
  } else if (businessContext) {
    businessHeader = `${name} - ${businessContext}`;
  }
  
  return {
    name,
    businessHeader,
    phone,
    email,
    company: recipientBusiness || contactInfo.company
  };
}

function extractCallerName(transcript: string): string | null {
  // Only analyze User lines from transcript to avoid grabbing assistant's name
  const userText = extractUserLines(transcript);
  if (!userText) return null;

  // Look for common patterns where callers introduce themselves
  const patterns = [
    /\b(?:this is|my name is|i'm|it's)\s+([A-Za-z][A-Za-z''-]+(?:\s+[A-Za-z][A-Za-z''-]+){0,2})\b/i,
    /tell\s+([A-Za-z][A-Za-z''-]+)\s+.*call.*back/i, // "Tell Doug to call me back"
    /have\s+([A-Za-z][A-Za-z''-]+)\s+call/i, // "have Aaron call you back"
  ];
  
  for (const pattern of patterns) {
    const match = userText.match(pattern);
    if (match && match[1] && match[1].length > 1) {
      // Capitalize first letter of each word
      return match[1].replace(/\b\w/g, l => l.toUpperCase());
    }
  }
  
  return null;
}

function extractUserLines(transcript: string): string {
  if (!transcript) return '';
  
  // Split transcript by lines and extract only User lines (case-insensitive)
  const lines = transcript.split('\n');
  const userLines = lines
    .filter(line => line.trim().toLowerCase().startsWith('user:'))
    .map(line => line.replace(/^user:\s*/i, '').trim())
    .filter(line => line.length > 0);
  
  return userLines.join(' ');
}

function extractPhoneFromTranscript(transcript: string): string | null {
  // Only analyze User lines
  const userText = extractUserLines(transcript);
  if (!userText) return null;

  if (process.env.NODE_ENV === 'development') {
    console.debug('Extracting phone from user text:', userText);
  }

  // Look for phone numbers spoken digit by digit (prioritize spaced digits)
  const spacedDigitPattern = /(?:\b\d\b[\s-]*){7,11}/g;
  const matches = userText.match(spacedDigitPattern);
  
  if (matches) {
    for (const match of matches) {
      const digits = match.replace(/\D/g, '');
      if (digits.length >= 7 && digits.length <= 11) {
        // Format as phone number
        if (digits.length === 10) {
          return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
        } else if (digits.length === 7) {
          return `${digits.slice(0,3)}-${digits.slice(3)}`;
        }
        return digits;
      }
    }
  }
  
  // Also look for traditional phone number formats
  const phonePattern = /\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;
  const phoneMatch = userText.match(phonePattern);
  if (phoneMatch) {
    return phoneMatch[0];
  }
  
  return null;
}

function isLikelyPersonName(str: string): boolean {
  if (!str) return false;
  
  // Reject common pronouns
  const pronouns = ['him', 'her', 'them', 'it', 'me', 'you', 'us'];
  if (pronouns.includes(str.toLowerCase())) {
    return false;
  }
  
  // Require first letter to be capitalized and at least 2 characters
  return /^[A-Z][a-z''-]+$/.test(str) && str.length >= 2;
}

function detectRecipient(userText: string): string | null {
  // Look for department mentions first
  const departmentPatterns = [
    /(?:talk to|speak with|reach|contact|with|for).*?\b(sales?)\b/i,
    /(?:talk to|speak with|reach|contact|with|for).*?\b(marketing)\b/i,
    /(?:talk to|speak with|reach|contact|with|for).*?\b(support)\b/i,
    /(?:talk to|speak with|reach|contact|with|for).*?\b(billing)\b/i,
    /(?:talk to|speak with|reach|contact|with|for).*?\b(accounting)\b/i,
    /(?:talk to|speak with|reach|contact|with|for).*?\b(service)\b/i,
    /\b(sales?)\s+department\b/i,
    /somebody\s+in\s+(?:the\s+)?(sales?|marketing|support|billing)\b/i
  ];
  
  for (const pattern of departmentPatterns) {
    const match = userText.match(pattern);
    if (match && match[1]) {
      const dept = match[1].toLowerCase();
      return dept === 'sales' || dept === 'sale' ? 'Sales' : 
             dept.charAt(0).toUpperCase() + dept.slice(1);
    }
  }
  
  // Look for specific person names with stricter validation
  const personPatterns = [
    /(?:talk to|speak with|reach|for|with)\s+([A-Z][a-z''-]+)/i,
    /have\s+([A-Z][a-z''-]+)\s+call\s+(?:me|you)\s+back/i,
    /tell\s+([A-Z][a-z''-]+)(?:\s+that|\s+to|\s+I)/i,
    /(?:sent|give)\s+(?:to\s+)?([A-Z][a-z''-]+)/i,
    /(?:calling|trying to reach)\s+([A-Z][a-z''-]+)/i
  ];

  for (const pattern of personPatterns) {
    const match = userText.match(pattern);
    if (match && match[1] && isLikelyPersonName(match[1])) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Found person recipient:', match[1]);
      }
      return match[1];
    }
  }
  
  return null;
}

function extractBusinessIntent(transcript: string): string | null {
  // Only analyze User lines
  const userText = extractUserLines(transcript);
  if (!userText) return null;

  const lowerUserText = userText.toLowerCase();

  if (process.env.NODE_ENV === 'development') {
    console.debug('Extracting business intent from user text:', userText);
  }

  // Enhanced business contexts with subject extraction
  const contexts = [
    { keywords: ['proposal', 'quote', 'estimate'], context: 'pricing inquiry' },
    { keywords: ['marketing', 'campaign', 'advertising'], context: 'marketing inquiry' },
    { keywords: ['billing', 'invoice', 'payment'], context: 'billing inquiry' },
    { keywords: ['appointment', 'meeting', 'schedule'], context: 'appointment request' },
    { keywords: ['support', 'help', 'issue', 'problem'], context: 'support request' },
    { keywords: ['order', 'purchase', 'buy'], context: 'order inquiry' },
    { keywords: ['service', 'maintenance', 'repair'], context: 'service request' },
    { keywords: ['information', 'details', 'question'], context: 'information request' },
  ];
  
  for (const { keywords, context } of contexts) {
    if (keywords.some(keyword => lowerUserText.includes(keyword))) {
      // Use detectRecipient to find who they want to talk to
      const recipient = detectRecipient(userText);
      
      if (process.env.NODE_ENV === 'development') {
        console.debug('Business context found:', context, 'Recipient:', recipient);
      }
      
      if (recipient) {
        return `${context} with ${recipient}`;
      }
      
      return context;
    }
  }
  
  return null;
}

function extractContactInfo(actions: CallAction[]): ContactInfo {
  const info: ContactInfo = {};
  
  // Extract phone from transcript first as fallback for derivePhones
  let transcriptPhone: string | null = null;
  for (const action of actions) {
    if (action.phrase && !transcriptPhone) {
      transcriptPhone = extractPhoneFromTranscript(action.phrase);
      if (transcriptPhone) break;
    }
  }
  
  for (const action of actions) {
    // First check resolved_intelligence if available (highest priority)
    if (action.resolved_intelligence?.caller_name && !info.name) {
      info.name = action.resolved_intelligence.caller_name;
    }
    
    // Extract from action_data
    if (action.action_data) {
      // For names, prefer caller_name
      if (action.action_data.caller_name && !info.name) {
        info.name = action.action_data.caller_name;
      }
      
      if (action.action_data.email && !info.email) {
        info.email = action.action_data.email;
      }
      if (action.action_data.company && !info.company) {
        info.company = action.action_data.company;
      }
    }
    
    // Extract from phrase using regex
    if (action.phrase && !info.email) {
      // First try to extract spoken email format
      const spokenEmail = extractEmailFromPhrase(action.phrase);
      if (spokenEmail) {
        info.email = spokenEmail;
      } else {
        // Fallback to standard email regex
        const emailMatch = action.phrase.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/);
        if (emailMatch) {
          info.email = emailMatch[0];
        }
      }
    }
  }
  
  // Use centralized phone derivation with transcript phone as fallback
  const phoneNumbers = derivePhones(actions, transcriptPhone);
  info.phone = phoneNumbers.primary;
  
  return info;
}

function extractEmailFromTranscript(transcript: string): string | null {
  const userText = extractUserLines(transcript);
  if (!userText) return null;

  // Look for spaced domain emails with more restrictive local part
  // Match patterns like "paul at roofing expert dot com" but not "really sorry paul at..."
  const spacedEmailPattern = /\b([\w.+-]+)\s+at\s+([\w.-]+(?:\s+[\w.-]+)*)\s+dot\s+([a-z]{2,})\b/gi;
  const spacedMatches = userText.toLowerCase().match(spacedEmailPattern);
  
  if (spacedMatches) {
    for (const match of spacedMatches) {
      const emailParts = match.match(/\b([\w.+-]+)\s+at\s+([\w.-]+(?:\s+[\w.-]+)*)\s+dot\s+([a-z]{2,})\b/i);
      if (emailParts) {
        const localPart = emailParts[1].trim();
        const domainPart = emailParts[2].replace(/\s+/g, '').trim();
        const extension = emailParts[3].trim();
        
        // Validate that local part looks reasonable (no punctuation at start/end)
        if (/^[\w][\w.+-]*[\w]$|^[\w]$/.test(localPart)) {
          const constructedEmail = `${localPart}@${domainPart}.${extension}`;
          
          if (process.env.NODE_ENV === 'development') {
            console.debug('Extracted spaced domain email:', match, '->', constructedEmail);
          }
          return constructedEmail;
        }
      }
    }
  }
  
  // Fallback to standard spoken email normalization
  let normalizedText = userText.toLowerCase()
    .replace(/\s+at\s+/g, '@')
    .replace(/\s+dot\s+/g, '.')
    .replace(/\s+dash\s+/g, '-')
    .replace(/\s+underscore\s+/g, '_')
    .replace(/\s+plus\s+/g, '+');

  // Look for standard email pattern
  const emailPattern = /\b[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}\b/g;
  const matches = normalizedText.match(emailPattern);
  
  if (matches) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('Extracted email from transcript:', userText, '->', matches[0]);
    }
    return matches[0];
  }

  return null;
}

function extractEmailFromPhrase(phrase: string): string | null {
  if (!phrase) return null;

  // Handle spaced domains with restrictive local part - "paul at roofing expert dot com"
  const spacedDomainPattern = /\b([\w.+-]+)\s+at\s+([\w.-]+(?:\s+[\w.-]+)*)\s+dot\s+([a-z]{2,})\b/i;
  const spacedMatch = phrase.match(spacedDomainPattern);
  
  if (spacedMatch) {
    const localPart = spacedMatch[1].trim();
    const domainPart = spacedMatch[2].replace(/\s+/g, '').trim();
    const extension = spacedMatch[3].trim();
    
    // Validate that local part looks reasonable
    if (/^[\w][\w.+-]*[\w]$|^[\w]$/.test(localPart)) {
      const email = `${localPart}@${domainPart}.${extension}`;
      
      if (process.env.NODE_ENV === 'development') {
        console.debug('Extracted spaced domain email:', phrase, '->', email);
      }
      return email;
    }
  }

  // Convert standard spoken email format to standard format
  // "edward at proroofer dot com" -> "edward@proroofer.com"
  const spokenEmailPattern = /\b([\w.+-]+)\s+at\s+([\w.-]+)\s+dot\s+([a-z]{2,})\b/i;
  const match = phrase.match(spokenEmailPattern);
  
  if (match) {
    const email = `${match[1]}@${match[2]}.${match[3]}`;
    if (process.env.NODE_ENV === 'development') {
      console.debug('Extracted spoken email:', phrase, '->', email);
    }
    return email;
  }

  // Look for standard email format
  const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
  const emailMatch = phrase.match(emailPattern);
  
  return emailMatch ? emailMatch[0] : null;
}